import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import TermsEmptyState from "./TermsEmptyState";

export const TERMS_SECTIONS = [
  {
    id: 1,
    title: "Acceptance of Terms",
    content: [
      "Welcome to the Autobet Lottery platform. By accessing and using this Platform, you agree to comply with and be bound by the following terms and conditions (\"Terms\"). If you do not agree with these Terms, please refrain from using the Platform.",
    ],
  },
  {
    id: 2,
    title: "User Registration and Responsibilities",
    items: [
      "To unlock the full potential of the Platform, including creating and participating in lotteries, users must register and provide accurate information.",
      "Users must possess a MetaMask browser extension or use the DApps (Decentralized Application) browser, configuring it to the BNB mainnet for seamless interactions.",
    ],
  },
  {
    id: 3,
    title: "Platform Features",
    items: [
      "Discover a spectrum of features on the Platform, spanning lotteries, gift cards, and real-time news updates.",
      "Dive into an immersive experience where users can explore lotteries, purchase tickets, and redeem gift cards, all in alignment with the Platform's guidelines.",
    ],
  },
  {
    id: 4,
    title: "Creator Registration and Responsibilities",
    items: [
      "Creators play a pivotal role; thus, they must register and submit a registration fee equivalent to 10% of the median between the minimum and maximum winning amounts for lotteries.",
      "Precision matters: Creators commit to providing accurate information during registration, covering details such as phone number, date of birth, email, and resident address.",
      "Referral Incentives: Creators have the opportunity to enter a referral address. If the referred address evolves into a creator, the referrer earns a 1% share of the new creator's profit.",
    ],
  },
  {
    id: 5,
    title: "Lottery Creation and Participation",
    items: [
      "Unleash creativity with various lottery types like Autobet Revolver, Autobet Mine, Autobet MRL, and Autobet Missile, each defined by unique rules and conditions.",
      "Empower creators during the lottery creation process, allowing them to specify winning amounts, ticket fees, and other pertinent details.",
      "User empowerment: Engage in thrilling lotteries by acquiring tickets that adhere to the prescribed rules.",
    ],
  },
  {
    id: 6,
    title: "Gift Cards",
    items: [
      "Creators hold the key to crafting gift cards, utilizing BET tokens, for either sale or donation, adding a dynamic layer to the Platform's ecosystem.",
      "User benefits: Buyers can seamlessly procure or redeem gift cards based on creator stipulations, with BET tokens transferred accordingly.",
    ],
  },
  {
    id: 7,
    title: "User Profile",
    items: [
      "Personalization at its best: Users can delve into their lottery and gift card history within their user profile.",
      "Financial flexibility: Withdraw BET tokens and relish referral bonuses earned through a user-friendly profile section.",
    ],
  },
  {
    id: 8,
    title: "Admin Responsibilities",
    items: [
      "The orchestrator of excellence: The platform owner (Admin) oversees the seamless execution of processes, managing creator details, lotteries, and partner relationships.",
      "Information hub: Admin retains the ability to withdraw platform earnings and curate news and announcements for enhanced user engagement.",
    ],
  },
  {
    id: 9,
    title: "Partnerships",
    items: [
      "Collaboration matters: Partners undergo a meticulous verification process and, once approved, seamlessly integrate into the platform.",
      "Mutual growth: Partners are entitled to a share percentage of the winning amount, as mutually agreed upon with the creator.",
    ],
  },
  {
    id: 10,
    title: "Notifications",
    items: [
      "Stay informed: The Platform ensures timely notifications related to lotteries, gift cards, news, and more, disseminated to users, creators, and admin.",
      "User-centric: Notifications can be conveniently exported in Excel format for enhanced user control and management.",
    ],
  },
  {
    id: 11,
    title: "Disclaimers and Limitation of Liability",
    items: [
      "Innovation meets responsibility: The platform delivers services \"as is,\" with users and creators acknowledging usage at their own risk. The Platform disclaims liability for any direct or indirect damages.",
    ],
  },
  {
    id: 12,
    title: "Governing Law",
    content: [
      "These Terms are governed by and construed in accordance with the laws of [your jurisdiction], ensuring a transparent and fair legal framework for all interactions on the Platform.",
    ],
  },
  {
    id: 13,
    title: "Changes to Terms",
    content: [
      "The Platform reserves the right to modify these Terms at any time. Users and creators will be duly notified of any changes, fostering an atmosphere of transparency and user awareness.",
    ],
  },
];

const TermsContentCard = ({ searchQuery = "", onClearSearch }) => {
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return TERMS_SECTIONS;
    const q = searchQuery.toLowerCase().trim();

    return TERMS_SECTIONS.filter((sec) => {
      const titleMatch = sec.title.toLowerCase().includes(q);
      const contentMatch =
        sec.content && sec.content.some((c) => c.toLowerCase().includes(q));
      const itemsMatch =
        sec.items && sec.items.some((item) => item.toLowerCase().includes(q));
      return titleMatch || contentMatch || itemsMatch;
    });
  }, [searchQuery]);

  if (filteredSections.length === 0) {
    return (
      <TermsEmptyState
        title="No Terms Clauses Found"
        description={`We couldn't find any clauses matching "${searchQuery}". Please refine your keyword or reset the search filter.`}
        actionText="Reset Search Filter"
        onAction={onClearSearch}
      />
    );
  }

  return (
    <Card
      sx={{
        p: { xs: 3, sm: 4.5 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        mb: 4,
      }}
    >
      <Stack spacing={4}>
        {filteredSections.map((sec, idx) => (
          <Box
            key={sec.id}
            id={`section-${sec.id}`}
            sx={{
              pt: idx !== 0 ? 3 : 0,
              borderTop: idx !== 0 ? `1px solid ${colors.dashboard.divider}` : "none",
            }}
          >
            {/* Section Header */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  minWidth: 32,
                  borderRadius: borderRadiusPx.sm,
                  bgcolor: colors.blue[50],
                  color: colors.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 900,
                  fontSize: "0.85rem",
                }}
              >
                {sec.id}
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: colors.slate[900],
                  fontSize: { xs: "1.15rem", sm: "1.25rem" },
                }}
              >
                {sec.title}
              </Typography>
            </Stack>

            {/* Direct Paragraph Content (e.g. Acceptance of Terms, Governing Law, Changes) */}
            {sec.content && (
              <Stack spacing={1.5} sx={{ pl: { xs: 0, sm: 5.5 } }}>
                {sec.content.map((text, cIdx) => (
                  <Typography
                    key={cIdx}
                    variant="body1"
                    sx={{
                      color: colors.slate[700],
                      lineHeight: 1.75,
                      fontSize: "0.95rem",
                    }}
                  >
                    {text}
                  </Typography>
                ))}
              </Stack>
            )}

            {/* Ordered Sub-Items (e.g. User Registration, Creators, Lotteries, Admin, etc.) */}
            {sec.items && (
              <Stack spacing={2} sx={{ pl: { xs: 0, sm: 5.5 } }}>
                {sec.items.map((itemText, iIdx) => (
                  <Box
                    key={iIdx}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: 2,
                      borderRadius: borderRadiusPx.lg,
                      bgcolor: colors.slate[50],
                      border: `1px solid ${colors.slate[200]}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        minWidth: 22,
                        borderRadius: borderRadiusPx.full,
                        bgcolor: colors.blue[50],
                        color: colors.primary.main,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.75rem",
                        fontWeight: 800,
                        mt: 0.25,
                      }}
                    >
                      {iIdx + 1}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.slate[700],
                        lineHeight: 1.7,
                        fontSize: "0.925rem",
                      }}
                    >
                      {itemText}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
    </Card>
  );
};

TermsContentCard.propTypes = {
  searchQuery: PropTypes.string,
  onClearSearch: PropTypes.func.isRequired,
};

export default TermsContentCard;
